/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from '../styles.module.scss';
import Hidden from '@/components/Hidden';
import { SearchPageTemplate } from '../SearchPageTemplate/SearchPageTemplate';
import { useSearchPartTypes } from '../../api/getSearchPartTypes';
import { SearchNoResults } from '../SearchComponent/SearchNoResults';
import { useDealDrawer } from '@/features/deals/hooks/useDealDrawer';
import { useRouter } from 'next/router';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { type HandleFilterType } from '@/types/filterTypes';
import { View } from '@az/starc-ui';

type Props = {
  viewingPartTypes: boolean;
  handleFilter?: (filter: HandleFilterType) => void;
};

export const BottomSection = ({ viewingPartTypes, handleFilter }: Props) => {
  const router = useRouter();
  const preferredVehicle = usePreferredVehicle();
  const { handleDealClick } = useDealDrawer();
  const { data: searchPartTypesData } = useSearchPartTypes({
    enabled: viewingPartTypes,
    viewingPartTypes,
  });

  const vehicleName = preferredVehicle?.vehicleName;
  const rppOptionsData = '12,24,36,48';
  const queryDisplayLabel =
    typeof router.query.searchText === 'string' ? router.query.searchText : '';

  return (
    <View className={styles.searchContentContainer}>
      <View className={styles.searchLeftContainer}>
        {searchPartTypesData?.records?.length ? (
          <Hidden mdDown={!viewingPartTypes}>
            <SearchPageTemplate
              viewingPartTypes={viewingPartTypes}
              onFilterSelect={handleFilter}
              content={[{ '@type': 'SearchLeftNav_FilterNav' }]}
            />
          </Hidden>
        ) : (
          <SearchPageTemplate
            onFilterSelect={handleFilter}
            viewingPartTypes={viewingPartTypes}
            content={[{ '@type': 'SearchLeftNav_CustomFallback' }]}
          />
        )}
      </View>
      <View className={styles.searchRightContainer}>
        {searchPartTypesData?.records?.length ? (
          <SearchPageTemplate
            content={[
              {
                '@type': 'SearchPageBody_YMME',
                desktop: true,
                layoutCode: 'Thin',
                mobile: true,
                name: 'YMME Content Block',
                tablet: true,
              },
              { '@type': 'SearchPageBody_AZCombinedSearchResults' },
            ]}
            queryDisplayLabel={queryDisplayLabel}
            viewingPartTypes={viewingPartTypes}
            onFilterSelect={handleFilter}
            handleDealClick={handleDealClick}
            rppOptionsData={rppOptionsData}
          />
        ) : (
          <>
            <SearchPageTemplate
              viewingPartTypes={viewingPartTypes}
              content={[
                {
                  '@type': 'SearchPageBody_YMME',
                  desktop: true,
                  layoutCode: 'Thin',
                  mobile: true,
                  name: 'YMME Content Block',
                  tablet: true,
                },
              ]}
              queryDisplayLabel={queryDisplayLabel}
              rppOptionsData={rppOptionsData}
            />
            <SearchNoResults vehicleName={vehicleName} queryDisplayLabel={queryDisplayLabel} />
          </>
        )}
      </View>
    </View>
  );
};
